import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionById from 'editor/src/store/design/selector/getConditionById';
import getMediaElementByUuid from 'editor/src/store/design/selector/getMediaElementByUuid';
import getStructureIndexByElementUuid from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { MediaText, TextConditionVariants } from 'editor/src/store/design/types';
import { getSharedTextFieldMembers } from 'editor/src/store/design/util/sharedTextFields';
import updateTextPropertiesWithoutRender from 'editor/src/store/design/util/updateTextPropertiesWithoutRender';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import loadFontOperation from 'editor/src/store/fonts/operation/loadFontOperation';
import type { Thunk } from 'editor/src/store/hooks';

import { captureException } from 'editor/src/logger';
import toastController from 'editor/src/util/toastController';

import { remeasureVariantsInFontOperation } from './applyTextVariantsForConditionOperation';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/**
 * Sets (or clears, with undefined) the per-option text variants on an element.
 *
 * When the edited variant belongs to the referenced condition's ACTIVE option,
 * the element's live text follows immediately — the author sees the label
 * change on canvas as they type the translation, instead of only after
 * flipping the dropdown away and back.
 */
const updateTextConditionVariantsOperation =
  (elementUuid: number, variants: TextConditionVariants | undefined): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const element = getMediaElementByUuid(state, elementUuid) as MediaText | undefined;
    const address = getStructureIndexByElementUuid(state, elementUuid);
    if (!element || element.type !== 'text' || !address) {
      return;
    }

    // Moving a text from one picklist to another has to go through clearing.
    // `values` is replaced wholesale, so a direct swap drops every string
    // authored for the outgoing picklist with nothing to show that it went.
    // Both panels already route through "off" / "None" for this reason
    // (VaryingTextsSection's disabled switch, PersonalizationText's
    // `conditionOptions`) — keeping the invariant here as well means a third
    // caller cannot quietly reintroduce the loss. Reported rather than ignored:
    // no UI path can reach it, so arriving here is a programming error.
    //
    // Only while the outgoing picklist is still IN the design. An id no
    // condition answers to has no owner left to lose its strings, and both
    // panels deliberately treat it as unassigned and offer the reassignment
    // (ownedByOtherPicklist, conditionOptions) — refusing it here would turn
    // that offer into a dead click and strand the data for good.
    const current = element.extra.textVariants;
    const outgoingCondition = current ? getConditionById(state, current.conditionId)?.condition : undefined;
    if (variants && current && outgoingCondition && current.conditionId !== variants.conditionId) {
      const strandedOptionCount = Object.keys(current.values).length;
      if (strandedOptionCount > 0) {
        captureException(new Error('Refused to reassign text variants between picklists'), {
          extra: {
            elementUuid,
            fromConditionId: current.conditionId,
            toConditionId: variants.conditionId,
            strandedOptionCount,
          },
        });
        return;
      }
    }

    // The mirror of the refusal in updateSharedTextFieldOperation: per-option
    // strings are the author's and a shared value is the buyer's, so a text
    // cannot hold both — each would overwrite the other on the next edit.
    // Guarded from this side too, or the pairing is simply reachable by
    // starting from the other end. Assigning only; clearing stays open, or a
    // template that already holds the pair could never be untangled.
    //
    // Only while the id names a group that still HAS another member — the same
    // narrowing the picklist refusal above needs, and for the same reason. The
    // panel calls a group of one "not sharing" (nothing to point at, and on an
    // ungated host no control at all), so it offers the picklists; refusing
    // here on the raw id would turn that offer into a dead click with no note
    // and no way out but joining something in order to leave it again.
    const { sharedFieldId } = element.extra;
    const sharedMembers = sharedFieldId ? getSharedTextFieldMembers(state.design.designData, sharedFieldId) : [];
    if (variants && sharedMembers.length > 1) {
      captureException(new Error('Refused to vary a text that shares its value'), {
        extra: { elementUuid, conditionId: variants.conditionId, sharedFieldId },
      });
      return;
    }

    // Clearing throws away what the author wrote for EVERY option, not just the
    // one on screen: "no longer varies" leaves nowhere in the model to park the
    // other strings. The neighbouring destructive controls here (remove option,
    // delete condition) all rely on undo rather than a confirmation, so match
    // that — but say how much went, or a mis-clicked switch is silent.
    const clearedOptionCount = variants ? 0 : Object.keys(element.extra.textVariants?.values ?? {}).length;

    // The active option's variant, if the author has already written one.
    const owningCondition = variants ? getConditionById(state, variants.conditionId)?.condition : undefined;
    const activeVariant = owningCondition && variants ? variants.values[owningCondition.activeOptionId] : undefined;

    const elementUpdate: Partial<MediaText> = {
      // The stale id goes with the same write. It named a group of one, which
      // is no group at all, and leaving it on a text that now varies would be
      // the one pairing every other path refuses — reachable again the moment
      // something joined it.
      extra: { ...element.extra, textVariants: variants, sharedFieldId: variants ? undefined : sharedFieldId },
    };
    let needsReflow = false;
    let introducedFont: string | undefined;
    if (activeVariant && elementUpdate.extra) {
      const fontFamily = activeVariant.fontFamily ?? elementUpdate.extra.fontFamily;
      elementUpdate.extra.text = activeVariant.text;
      elementUpdate.extra.fontFamily = fontFamily;
      // The font counts as much as the string: it changes the measured width,
      // so a row packed around the outgoing metrics has to re-pack even when
      // the text itself is identical.
      needsReflow = activeVariant.text !== element.extra.text || fontFamily !== element.extra.fontFamily;
      if (fontFamily !== element.extra.fontFamily) {
        introducedFont = fontFamily;
      }
      // A swapped string invalidates the cached glyph outline exactly as a
      // canvas edit does (see updateMediaElementOperation); left in place it
      // keeps painting the outgoing language. Both faces are checked because
      // the font can travel with the variant.
      if (needsReflow && (element.extra.fontFamily.startsWith('custom-') || fontFamily.startsWith('custom-'))) {
        elementUpdate.path = undefined;
      }
      updateTextPropertiesWithoutRender(element, elementUpdate, undefined);
    }

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Text variants updated'));
      dispatch(updateMediaElementAction({ elementAddress: address, elementUpdate }));
      if (needsReflow) {
        dispatch(updateHiddenElementsOperation(address.spreadIndex));
      }
    });

    if (clearedOptionCount > 1) {
      toastController.warning(
        i18n.t('editor-text-variants-cleared', 'Per-option text removed'),
        i18n.t(
          'editor-text-variants-cleared-message',
          'The text this label kept for each option was cleared. Undo restores it.',
        ),
      );
    }

    // Same reason as the option swap in applyTextVariantsForConditionOperation:
    // the measurement above used whatever face the browser had at the time, so
    // re-measure once the real one lands. A no-op when it was already loaded.
    const conditionId = variants?.conditionId;
    if (introducedFont && conditionId) {
      const fontFamily = introducedFont;
      void Promise.resolve(dispatch(loadFontOperation(fontFamily))).then(() =>
        dispatch(remeasureVariantsInFontOperation(conditionId, fontFamily)),
      );
    }
  };

export default updateTextConditionVariantsOperation;
